<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  loading: { type: Boolean, default: false },
  responsibilities: { type: Array, default: () => [] },
});

const emit = defineEmits(['boleto', 'payment']);
const { t } = useI18n();
const paymentGroups = computed(() =>
  ['Vendedor', 'Comprador']
    .map(role => ({
      role,
      responsibilities: props.responsibilities.filter(
        responsibility => responsibility.papel === role
      ),
    }))
    .filter(group => group.responsibilities.length)
);
const hasCompleteBoleto = responsibility =>
  Boolean(
    responsibility.boleto?.enviado &&
      responsibility.data_emissao &&
      responsibility.data_envio &&
      responsibility.data_validade
  );
const paymentCount = responsibilities => {
  const confirmed = responsibilities.filter(
    responsibility => responsibility.comfirmacao_pagamento === 1
  ).length;
  return `${confirmed}/${responsibilities.length}`;
};
const paymentStatusClass = role =>
  role === 'Vendedor'
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-blue-100 text-blue-700';
const paymentIsOverdue = responsibility => {
  if (
    !responsibility.data_validade ||
    responsibility.comfirmacao_pagamento === 1
  ) {
    return false;
  }
  return new Date(`${responsibility.data_validade}T12:00:00`) < new Date();
};
</script>

<template>
  <section
    class="rounded-lais border border-lais-border-soft bg-white p-6 shadow-lais-card sm:p-8"
  >
    <h2 class="font-redHatDisplay text-xl font-semibold text-lais-text">
      {{ t('REAL_ESTATE_FINANCE.STATUS_UPDATE.PAYMENT_TITLE') }}
    </h2>
    <div v-for="group in paymentGroups" :key="group.role" class="mt-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <p class="font-semibold text-lais-text">{{ group.role }}</p>
        <span
          class="rounded-full px-3 py-1 text-xs font-bold"
          :class="paymentStatusClass(group.role)"
        >
          {{ paymentCount(group.responsibilities) }}
        </span>
      </div>
      <div class="mt-3 divide-y divide-lais-border-soft">
        <article
          v-for="responsibility in group.responsibilities"
          :key="responsibility.id"
          class="grid gap-3 py-4 first:pt-0 sm:grid-cols-[auto_minmax(0,1fr)_auto_auto] sm:items-center"
          :class="!hasCompleteBoleto(responsibility) && 'opacity-50'"
        >
          <input
            :checked="responsibility.comfirmacao_pagamento === 1"
            :disabled="loading || !hasCompleteBoleto(responsibility)"
            :aria-label="responsibility.usuario_nome"
            type="checkbox"
            class="size-5 rounded border-lais-border text-lais-primary focus:ring-lais-primary"
            @change="emit('payment', responsibility)"
          />
          <div class="min-w-0">
            <p class="truncate font-semibold text-lais-text">
              {{ responsibility.usuario_nome }}
            </p>
            <p class="mt-1 text-sm text-lais-text-muted">
              {{ responsibility.valor_pagamento?.amount }}
            </p>
          </div>
          <button
            v-if="hasCompleteBoleto(responsibility)"
            type="button"
            class="text-left text-sm font-semibold text-lais-primary hover:underline"
            @click="emit('boleto', responsibility)"
          >
            {{ responsibility.boleto?.nome }}
          </button>
          <span
            v-else
            class="w-fit rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600"
          >
            {{ t('REAL_ESTATE_FINANCE.STATUS_UPDATE.BOLETO_MISSING') }}
          </span>
          <span
            v-if="paymentIsOverdue(responsibility)"
            class="w-fit rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800"
          >
            {{ t('REAL_ESTATE_FINANCE.STATUS_UPDATE.OVERDUE') }}
          </span>
        </article>
      </div>
    </div>
  </section>
</template>
